import { useState, type FormEvent } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import { Ban, Trash2 } from 'lucide-react';
import { useBlockIp, useBlockedIps, useUnblockIp } from '../../../hooks/admin/useAdminSecurity';
import { EXPIRY_PRESETS, expiryPresetToHours, isValidIp, type ExpiryPreset } from '../../../lib/adminFilters';
import { getApiErrorCode, getApiErrorMessage } from '../../../lib/apiError';
import { formatDateTime } from '../../../lib/utils';
import Button from '../../../components/ui/Button';
import ExportJsonButton from '../../../components/admin/ExportJsonButton';
import { listBlockedIps } from '../../../lib/adminApi';
import { securityExportFilename } from '../../../lib/adminSecurityExport';
import DataTable from '../../../components/ui/DataTable';
import ConfirmActionDialog from '../../../components/admin/ConfirmActionDialog';
import InlineNotice from '../../../components/admin/InlineNotice';
import type { BlockedIp } from '../../../types/admin';

interface BlockedIpsTabProps {
  selfIp?: string | null;
}

export default function BlockedIpsTab({ selfIp }: BlockedIpsTabProps) {
  const listQuery = useBlockedIps();
  const blockMutation = useBlockIp();
  const unblockMutation = useUnblockIp();

  const [ip, setIp] = useState('');
  const [reason, setReason] = useState('');
  const [expiry, setExpiry] = useState<ExpiryPreset>('24h');
  const [formError, setFormError] = useState<string | null>(null);
  const [selfRefused, setSelfRefused] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [removing, setRemoving] = useState<BlockedIp | null>(null);

  const submit = async (e: FormEvent, force = false) => {
    e.preventDefault();
    setFormError(null);
    setNotice(null);
    const value = ip.trim();
    if (!isValidIp(value)) {
      setFormError('Enter a valid IPv4, IPv4 CIDR (e.g. 203.0.113.0/24) or IPv6 address.');
      return;
    }
    try {
      await blockMutation.mutateAsync({
        ip: value,
        reason: reason.trim() || undefined,
        expiresInHours: expiryPresetToHours(expiry),
        force: force || undefined,
      });
      setNotice({ tone: 'success', text: `${value} is now blocked${expiry === 'never' ? '' : ` for ${EXPIRY_PRESETS.find((p) => p.value === expiry)?.label}`}.` });
      setIp('');
      setReason('');
      setSelfRefused(false);
    } catch (err) {
      if (getApiErrorCode(err) === 'SELF_IP') {
        setSelfRefused(true);
        setFormError('That is your own IP address. Blocking it will lock you out of this admin.');
      } else {
        setFormError(getApiErrorMessage(err, 'Failed to block IP'));
      }
    }
  };

  const remove = async () => {
    if (!removing) return;
    try {
      await unblockMutation.mutateAsync({ id: removing.id });
      setNotice({ tone: 'success', text: `${removing.ip} unblocked.` });
      setRemoving(null);
    } catch {
      /* shown in dialog */
    }
  };

  const items = listQuery.data?.items ?? [];
  const looksLikeSelf = Boolean(selfIp && ip.trim() === selfIp);

  return (
    <div className="space-y-4">
      <form onSubmit={(e) => void submit(e)} className="glass-card space-y-3 p-4">
        <h3 className="text-sm font-semibold text-glass-text">Block an IP address</h3>
        <div className="grid gap-3 md:grid-cols-[1.2fr_2fr_1fr_auto]">
          <input
            value={ip}
            onChange={(e) => {
              setIp(e.target.value);
              setSelfRefused(false);
              setFormError(null);
            }}
            placeholder="203.0.113.7 or 203.0.113.0/24"
            className="glass-input w-full px-3 py-2 font-mono text-sm"
            aria-label="IP address"
          />
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional, stored in the audit log)"
            className="glass-input w-full px-3 py-2 text-sm"
            aria-label="Reason"
          />
          <select value={expiry} onChange={(e) => setExpiry(e.target.value as ExpiryPreset)} className="glass-input w-full px-3 py-2 text-sm" aria-label="Expiry">
            {EXPIRY_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          <Button type="submit" variant="destructive" disabled={blockMutation.isPending || !ip.trim()}>
            <Ban className="mr-2 h-4 w-4" aria-hidden />
            Block
          </Button>
        </div>
        {looksLikeSelf && !selfRefused && (
          <InlineNotice tone="warn">This looks like your own IP ({selfIp}). The API will refuse unless you force it.</InlineNotice>
        )}
        {formError && (
          <InlineNotice tone="error" onDismiss={() => setFormError(null)}>
            <span>{formError}</span>
            {selfRefused && (
              <button
                type="button"
                onClick={(e) => void submit(e as unknown as FormEvent, true)}
                className="ml-3 rounded-md border border-red-500/40 px-2 py-0.5 text-xs font-semibold text-red-600 hover:bg-red-500/10"
                disabled={blockMutation.isPending}
              >
                Block my own IP anyway
              </button>
            )}
          </InlineNotice>
        )}
        <p className="text-xs text-glass-text-3">
          Loopback and private ranges are refused by the API. Blocks propagate to all API instances within about a minute.
          WordPress, Laravel log, and dumped <code className="font-mono">.env</code> / <code className="font-mono">.git</code> probes
          are blocked automatically on the first request. Locked out? Delete the row from{' '}
          <code className="font-mono">BlockedIp</code> in the database.
        </p>
      </form>

      <div className="flex justify-end">
        <ExportJsonButton
          label="Download this tab"
          filename={() => securityExportFilename('blocked-ips')}
          collect={async () => {
            const { items } = await listBlockedIps();
            return { exportedAt: new Date().toISOString(), selfIp, items };
          }}
        />
      </div>

      {notice && (
        <InlineNotice tone={notice.tone} onDismiss={() => setNotice(null)}>
          {notice.text}
        </InlineNotice>
      )}
      {listQuery.isError && <InlineNotice tone="error">{getApiErrorMessage(listQuery.error, 'Failed to load blocked IPs')}</InlineNotice>}

      <div className="glass-card overflow-hidden">
        <DataTable<BlockedIp>
          data={items}
          isLoading={listQuery.isPending}
          emptyMessage="No IPs are blocked."
          columns={[
            {
              header: 'IP',
              accessor: (b) => (
                <span className="font-mono text-xs text-glass-text">
                  {b.ip}
                  {selfIp && b.ip === selfIp && <span className="ml-2 text-[11px] text-[rgb(var(--danger))]">(you)</span>}
                </span>
              ),
            },
            {
              header: 'Reason',
              accessor: (b) => (
                <span className="block max-w-[320px] truncate text-glass-text-2" title={b.reason ?? undefined}>
                  {b.reason || '—'}
                </span>
              ),
            },
            {
              header: 'Expires',
              accessor: (b) =>
                b.expiresAt ? (
                  <span title={formatDateTime(b.expiresAt)} className={new Date(b.expiresAt).getTime() < Date.now() ? 'text-glass-text-3' : 'text-glass-text-2'}>
                    {new Date(b.expiresAt).getTime() < Date.now() ? 'expired' : `in ${formatDistanceToNowStrict(new Date(b.expiresAt))}`}
                  </span>
                ) : (
                  <span className="text-glass-text-2">never</span>
                ),
            },
            {
              header: 'Created',
              accessor: (b) => (
                <span className="text-glass-text-2">
                  {formatDateTime(b.createdAt)}
                  {b.createdBy?.fullName ? (
                    <span className="block text-xs text-glass-text-3">by {b.createdBy.fullName}</span>
                  ) : b.reason?.startsWith('Auto-blocked') ? (
                    <span className="block text-xs text-glass-text-3">auto-block</span>
                  ) : null}
                </span>
              ),
            },
            {
              header: 'Hits 24h',
              accessor: (b) => <span className={`tabular-nums ${b.hits24h > 0 ? 'text-[rgb(var(--warn))]' : 'text-glass-text-2'}`}>{b.hits24h.toLocaleString()}</span>,
            },
            {
              header: '',
              accessor: (b) => (
                <button type="button" onClick={() => setRemoving(b)} className="btn-glass inline-flex items-center gap-1 px-2.5 py-1 text-xs">
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  Remove
                </button>
              ),
            },
          ]}
        />
      </div>

      <ConfirmActionDialog
        isOpen={Boolean(removing)}
        onClose={() => {
          setRemoving(null);
          unblockMutation.reset();
        }}
        title={`Unblock ${removing?.ip ?? ''}`}
        description="Requests from this address will be accepted again within about a minute."
        confirmLabel="Unblock"
        isPending={unblockMutation.isPending}
        error={unblockMutation.isError ? getApiErrorMessage(unblockMutation.error, 'Failed to unblock') : null}
        onConfirm={remove}
      />
    </div>
  );
}
